// Forms Service
import { apiClient } from './api-client';
import type { Form, FormsResponse, SaveFormRequest } from '../types/form.types';

export const formsService = {
    // Get all forms
    async getForms(params?: {
        page?: number;
        perPage?: number;
        search?: string;
        formType?: string;
        status?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    }): Promise<FormsResponse> {
        const response = await apiClient.callProxy('forms', 'getForms', params);

        // Status mapping: convert numeric values to string representations
        const statusMap: Record<number, string> = {
            0: 'unpublished',
            1: 'published',
            2: 'archived',
            3: 'trashed',
        };

        // Transform snake_case keys from backend to camelCase for frontend
        if (response.items) {
            response.items = response.items.map((form: any) => ({
                ...form,
                formType: form.form_type || form.formType,
                status: typeof form.status === 'number' ? statusMap[form.status] || 'unpublished' : form.status,
                createdDate: form.created || form.createdDate,
                modifiedDate: form.modified || form.modifiedDate,
                createdBy: form.created_by || form.createdBy,
                modifiedBy: form.modified_by || form.modifiedBy,
            }));
        }

        return response;
    },

    // Get single form
    async getForm(id: number): Promise<Form> {
        const form = await apiClient.callProxy('forms', 'getForm', { id });

        // Transform snake_case keys from backend to camelCase for frontend
        return {
            ...form,
            formType: form.form_type || form.formType,
            createdDate: form.created || form.createdDate,
            modifiedDate: form.modified || form.modifiedDate,
            createdBy: form.created_by || form.createdBy,
            modifiedBy: form.modified_by || form.modifiedBy,
        };
    },

    // Save form (create or update)
    async saveForm(data: SaveFormRequest): Promise<Form> {
        return apiClient.callProxy('forms', 'saveForm', {data: data});
    },

    // Delete form
    async deleteForm(id: number): Promise<{ success: boolean }> {
        return apiClient.callProxy('forms', 'deleteForm', { id }, 'POST');
    },

    // Clone form
    async cloneForm(id: number): Promise<Form> {
        return apiClient.callProxy('forms', 'cloneForm', { id });
    },

    // Get field types
    async getFieldTypes(): Promise<any> {
        return apiClient.callProxy('forms', 'getFieldTypes');
    },

    // Get a flat list of fields for one form (for the email-notification dialog,
    // recipient picker, placeholder hints). Returns rows with the canonical
    // integer DB id so they round-trip back as recipient_field_id.
    async getFormFields(formId: number): Promise<{ items: Array<{
        id: number;
        field_name: string;
        label: string;
        field_type: string;
        field_order: number;
    }> }> {
        return apiClient.callProxy('forms', 'getFormFields', { formId });
    },

    // Publish/Unpublish form
    async toggleStatus(id: number, status: string): Promise<Form> {
        return apiClient.callProxy('forms', 'toggleFormStatus', { id, status });
    },

    // Get form statistics
    async getFormStats(id: number): Promise<any> {
        return apiClient.callProxy('forms', 'getFormStats', { id });
    },
};
